<template>
  <div
    :class="
      'd-flex justify-space-between align-center pl-2 pr-2 mt-3 ' + statusClass
    "
    :id="id"
  >
    <div v-if="safe" class="d-flex">
      <v-icon class="mr-2">mdi-paperclip</v-icon>
      <div>
        <span :class="{ 'font-weight-bold': isNewFile }">{{ fileName }}</span>
        <v-chip
          v-if="isNewFile"
          class="ml-2"
          small
          label
          color="red"
          text-color="white"
          data-testid="new-label"
        >
          <strong>NEW</strong>
        </v-chip>
        <br />
        <span
          v-if="docReceived"
          class="timestamp-info"
          data-testid="doc-received-info"
          >{{ receivedOnInfo }}</span
        >
      </div>
    </div>

    <div v-else-if="pendingUpload" class="d-flex pending-upload-status">
      <v-icon class="mr-2">mdi-timer-sand-empty</v-icon>
      <div>
        <span
          >Documents will appear here once the parent/carer uploads them.</span
        >
        <br />
        <span class="timestamp-info">{{ enableDocsRequestByInfo }}</span>
      </div>
    </div>

    <span v-else-if="infected" class="orange--text text--darken-1 pt-2">
      <v-icon color="orange darken-1" class="mr-1">mdi-alert-circle</v-icon>
      <strong>{{ fileName }}</strong> - this file was infected and cannot be
      viewed
    </span>

    <span v-else-if="toBeScanned" class="orange--text text--darken-1 pt-2">
      <v-icon color="orange darken-1" class="pr-1">mdi-autorenew</v-icon>
      <strong>{{ fileName }}</strong> - this file has not been virus scanned yet
      and cannot be viewed
    </span>

    <span v-else-if="missing" class="orange--text text--darken-1 pt-2">
      <v-icon color="orange darken-1" class="pr-1">mdi-alert-circle</v-icon>
      <strong>{{ fileName }}</strong> - this file is unavailable and cannot be
      viewed
    </span>

    <span v-else-if="noStatus" class="orange--text text--darken-1 pt-2">
      <v-icon color="orange darken-1" class="pr-1">mdi-alert-circle</v-icon>
      <strong>{{ fileName }}</strong> - this file cannot currently be retrieved
    </span>
    <div>
      <AdsButton
        v-if="canView && safe"
        secondary
        button-text="Download"
        icon="mdi-download-outline"
        :aria-label="ariaLabelDownload"
        class="mr-3"
        @click="onDownloadClick($event, value)"
        data-testid="download-button"
      />
      <AdsButton
        v-if="canView && safe"
        secondary
        button-text="View"
        icon="mdi-eye-outline"
        :aria-label="ariaLabelView"
        @click="onViewClick($event, value)"
        data-testid="view-button"
      />
      <DownloadDocumentsModal
        v-if="displayDownloadDialog"
        :applications="applicationInfo"
        :school-code="schoolCode"
        :application-type="applicationType"
        :document="document"
        :skip-download-app="true"
        @close="displayDownloadDialog = false"
      />
    </div>
  </div>
</template>

<script>
import { AdsButton } from '@nswdoe/doe-ui-core'
import { STREAM, FILE_STATUSES } from '@/constants'
import DownloadDocumentsModal from '@/components/applicationList/DownloadDocumentsModal.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'AppFileEditor',
  components: { AdsButton, DownloadDocumentsModal },
  props: {
    field: Object,
    id: String,
    value: String, // Path to the file
    handleView: Function, // Passed in function to view the file,
    canView: Boolean, // Control viewability of the file
    status: String, // 'SAFE', 'INFECTED-REPLACED' or 'UNSCANNED' to control the message for an infected file.
    isAdditionalDocs: Boolean,
    docReceived: String,
    docRecordNo: Number,
    isDocViewed: Boolean,
    isDisplayNewLabel: Boolean
  },
  data() {
    return {
      document: null,
      displayDownloadDialog: false
    }
  },
  computed: {
    ...mapGetters(['currentStream']),
    isArchived() {
      return this.currentStream === STREAM.ARCHIVE
    },
    isNewFile() {
      return this.isDisplayNewLabel && !this.isDocViewed && !this.isArchived
    },
    ariaLabelDownload() {
      return 'Download ' + this.fileName
    },
    ariaLabelView() {
      return 'View ' + this.fileName
    },
    fileName() {
      return this.value.split('/').slice(-1)[0]
    },
    infected() {
      return this.status === FILE_STATUSES.INFECTED
    },
    safe() {
      return this.status === FILE_STATUSES.SAFE
    },
    toBeScanned() {
      return this.status === FILE_STATUSES.TO_BE_SCANNED
    },
    missing() {
      return this.status === FILE_STATUSES.MISSING
    },
    pendingUpload() {
      return this.status === FILE_STATUSES.PENDING_UPLOAD
    },
    noStatus() {
      return this.status === undefined
    },
    statusClass() {
      return this.status === undefined ? '' : this.status.toLowerCase()
    },
    application() {
      return this.$store.getters.application
    },
    schoolCode() {
      return this.application.schoolCode
    },
    applicationType() {
      return this.$store.state.applicationType
    },
    applicationInfo() {
      return [
        {
          applicationID: this.application.applicationID,
          studentFirstName: this.application.student.firstName,
          studentFamilyName: this.application.student.familyName,
          supportingDocuments: this.application.supportingDocuments,
          isDigitizeEOIConsiderationDocUploaded:
            this.application.isDigitizeEOIConsiderationDocUploaded
        }
      ]
    },
    enableDocsRequestByInfo() {
      const { additionalDocRequestBy, additionalDocRequestDatetime } =
        this.application
      return `Enabled by ${additionalDocRequestBy} on ${this.$moment(
        additionalDocRequestDatetime
      ).format('LLLL')}`
    },
    receivedOnInfo() {
      return `Received on ${this.$moment(this.docReceived).format('LLLL')}`
    }
  },
  methods: {
    onViewClick(e, value) {
      this.handleView({
        filePath: value,
        previewMode: 'INLINE',
        docRecordNo: this.docRecordNo
      })
    },
    onDownloadClick(e, value) {
      this.displayDownloadDialog = true
      this.document = {
        filePath: value,
        previewMode: 'ATTACHMENT',
        docRecordNo: this.docRecordNo,
        isAdditionalDocs: this.isAdditionalDocs,
        isDocViewed: this.isDocViewed
      }
    }
  }
}
</script>

<style scoped lang="scss">
.pending-upload {
  box-shadow: 0 1px 3px #c5c5c5;
  margin: 8px 16px;
  .pending-upload-status {
    width: 100%;
    padding: 16px 0;
  }
}
.timestamp-info {
  font-size: 14px;
  color: $color-text-light;
}
</style>
